import { type FC, useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useGetWorkflow, useListWorkflowExecutions, type PlainWorkflow } from "@/queries/automation-queries";
import {
    workflowName,
    workflowsUrl,
    hasManualTrigger,
    isWorkflowDeleting,
    workflowDetailsEditUrl,
    WorkflowDetailsTab,
} from "@/utils/automations";
import { WorkflowExecutionsList } from "@/components/automations/AutomationExecutionsList";
import { WorkflowActionsDropdown } from "@/components/automations/AutomationActionsDropdown";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Text } from "@/components/podkit/typography/Text";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/podkit/tabs/Tabs";
import { useMembers } from "@/queries/organization-queries";
import Breadcrumbs from "@/components/Breadcrumb";
import { BreadcrumbsSkeleton } from "@/components/BreadcrumbsSkeleton";
import { AutomationPropertiesHeader } from "@/routes/automations/AutomationPropertiesHeader";
import { AutomationDefinitionSectionView } from "@/routes/automations/automation-details-page/AutomationDefinitionSection";
import { Button } from "@/components/flexkit/Button";
import { withGitpodSize } from "@/assets/icons/utils";
import IconPencil from "central-icons/IconPencil";
import { RunAutomationButton } from "@/routes/automations/automation-details-page/RunAutomationButton";
import { Banner } from "@/components/flexkit/Banner";
import { convertWorkflowActionToFormSteps } from "@/components/automations/automation-edit-form-data";
import { useSetAtom } from "jotai";
import { editableStepsAtom, isEditingStepsAtom } from "@/routes/automations/automation-details-page/state";
import AutomationEditDialog from "@/components/automations/AutomationEditDialog";

const IconPencilWithSize = withGitpodSize(IconPencil);

export const AutomationDetailsPage: FC = () => {
    const { workflowId } = useParams<{ workflowId: string }>();
    const navigate = useNavigate();
    const { data: workflow, error: workflowError } = useGetWorkflow(workflowId);
    const {
        data: workflowExecutions,
        error: workflowExecutionsError,
        refetch: refetchExecutions,
    } = useListWorkflowExecutions({
        enabled: !!workflowId,
        workflowId: workflowId || "",
    });
    const { data: members } = useMembers();
    const [selectedTab, setSelectedTab] = useState(WorkflowDetailsTab.Executions);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

    const location = useLocation();
    useEffect(() => {
        switch (location.hash) {
            case `#${WorkflowDetailsTab.Definition}`:
                setSelectedTab(WorkflowDetailsTab.Definition);
                break;
            case `#${WorkflowDetailsTab.Executions}`:
                setSelectedTab(WorkflowDetailsTab.Executions);
                break;
            default:
                // if no hash is set, navigate to the executions tab
                navigate(`#${WorkflowDetailsTab.Executions}`, { replace: true });
        }
    }, [location.hash, navigate]);

    const setIsEditingSteps = useSetAtom(isEditingStepsAtom);
    const setEditableSteps = useSetAtom(editableStepsAtom);
    useEffect(() => {
        setIsEditingSteps(false);
        setEditableSteps(null);
    }, [setEditableSteps, setIsEditingSteps]);

    const changeTab = (value: string) => {
        navigate(`#${value}`, { replace: true });
    };

    const isDeleting = workflow ? isWorkflowDeleting(workflow) : false;
    const steps = useMemo(() => convertWorkflowActionToFormSteps(workflow?.spec?.action), [workflow?.spec?.action]);

    return (
        <div data-testid="workflow-page" className="flex min-h-full flex-col gap-4">
            {workflow ? (
                <div className="flex flex-col gap-2">
                    {/* Header row: breadcrumb + action buttons */}
                    <div className="flex items-center justify-between">
                        <Breadcrumbs
                            customBreadcrumbs={[
                                { label: "Automations", to: workflowsUrl() },
                                { label: workflowName(workflow) },
                            ]}
                        />
                        <div className="flex items-center gap-2">
                            {/* We don't show the run action in the dropdown as we have a dedicated run button next to it */}
                            <WorkflowActionsDropdown workflow={workflow} showRunAction={false} buttonSize="md" />
                            <AutomationDetailsPageActionButtons workflow={workflow} />
                        </div>
                    </div>
                    {/* Description and trigger info */}
                    <div className="flex flex-col gap-1">
                        <Text className="text-base text-content-strong">
                            {workflow.metadata?.description || "No description provided."}
                        </Text>
                        <AutomationPropertiesHeader
                            workflow={workflow}
                            editable={true}
                            onEdit={() => {
                                setIsEditDialogOpen(true);
                            }}
                        />
                    </div>
                </div>
            ) : (
                <BreadcrumbsSkeleton itemsCount={2} />
            )}

            {isDeleting && (
                <Banner
                    variant="info"
                    className="mt-4"
                    text="This automation is being deleted. All interactions are disabled."
                />
            )}

            <ErrorMessage error={workflowError} />

            <Tabs className="flex grow flex-col" value={selectedTab} onValueChange={changeTab}>
                <TabsList className="mb-6 w-fit">
                    <TabsTrigger value={WorkflowDetailsTab.Executions}>Executions</TabsTrigger>
                    <TabsTrigger value={WorkflowDetailsTab.Definition}>Definition</TabsTrigger>
                </TabsList>

                <TabsContent value={WorkflowDetailsTab.Definition} className="flex flex-col data-[state=active]:grow">
                    {workflow ? <AutomationDefinitionSectionView workflowId={workflow.id} steps={steps} /> : null}
                </TabsContent>

                <TabsContent value={WorkflowDetailsTab.Executions} className="flex flex-col data-[state=active]:grow">
                    {workflow ? (
                        <WorkflowExecutionsList
                            executions={workflowExecutions || []}
                            members={members?.members}
                            workflow={workflow}
                            hasManualTrigger={hasManualTrigger(workflow)}
                            error={workflowExecutionsError}
                            onRetry={() => refetchExecutions()}
                        />
                    ) : null}
                </TabsContent>
            </Tabs>

            <AutomationEditDialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen} workflow={workflow} />
        </div>
    );
};

const AutomationDetailsPageActionButtons: FC<{ workflow: PlainWorkflow }> = ({ workflow }) => {
    const navigate = useNavigate();

    const handleEdit = useCallback(() => {
        navigate(workflowDetailsEditUrl({ id: workflow.id }));
    }, [navigate, workflow.id]);

    return (
        <div className="flex items-center gap-2">
            <Button variant="outline" LeadingIcon={IconPencilWithSize} onClick={handleEdit} data-tracking-id="edit">
                Edit
            </Button>
            {hasManualTrigger(workflow) && <RunAutomationButton workflowId={workflow.id} data-tracking-id="run" />}
        </div>
    );
};
